import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import ModalAttachFiles from "../../modal/modalAttachFiles";
import ModalDeleteFile from "../../modal/modalDeleteFile";
import FileIcon from "../utiles/FileIcon";
import { useNavigate } from "react-router-dom";
import useMessagesApi from "../../../hooks/api/useMessagesApi";

const MessageForm = ({
  mode = 'create',
  onSuccess,
  onDiscard,
  onEdit,
  initialData = null,
  messageId = null  
}) => {
  const { createMessage, updateMessage } = useMessagesApi();
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [filePaths, setFilePaths] = useState(
    (initialData && initialData.file_paths) || ''
  );
  const [filePathsData, setFilePathsData] = useState(
    (initialData && initialData.file_paths_array) || []
  );
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDeleteIndex, setFileToDeleteIndex] = useState(null);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    label: initialData?.label || '',
    recipient_email: initialData?.recipient_email || '',
    subject: initialData?.subject || '',
    body: initialData?.body || '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        label: initialData.label || '',
        recipient_email: initialData.recipient_email || '',
        subject: initialData.subject || '',
        body: initialData.body || '',
      });
      setFilePaths(initialData.file_paths || '');
      // Store file_paths array if available (contains size info)
      setFilePathsData(initialData.file_paths_array || []);
    } else {
      setFormData({
        label: '',
        recipient_email: '',
        subject: '',
        body: '',
      });
      setFilePaths('');
      setFilePathsData([]);
    }
  }, [initialData, messageId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFolderSelect = (selectedFolderPaths) => {
    setFilePaths(selectedFolderPaths.join(","));
    toast.success('Files attached successfully');
  };

  const handleApproveClick = async () => {
    const { subject, recipient_email, body } = formData;
    setLoading(true);
    try {
      // Normalize file_paths: add / prefix for folders
      const normalizedFilePaths = filePaths
        ? filePaths
            .split(',')
            .map((path) => {
              const trimmedPath = (path || '').trim();
              if (!trimmedPath) return '';

              const pathWithoutLeadingSlash = trimmedPath.replace(/^\/+/, '');
              if (!pathWithoutLeadingSlash) return '';

              const hasExtension = /\.\w+$/.test(pathWithoutLeadingSlash);
              const isFileInData = filePathsData.some((file) => {
                const filePath = (file.path || '').replace(/^\/+/, '');
                return (
                  filePath === pathWithoutLeadingSlash ||
                  filePath.endsWith(pathWithoutLeadingSlash) ||
                  pathWithoutLeadingSlash.endsWith(filePath)
                );
              });

              const isFolder = !hasExtension && !isFileInData;

              if (isFolder) {
                const withoutTrailing = pathWithoutLeadingSlash.replace(/\/+$/, '');
                if (!withoutTrailing) return '';
                return `${withoutTrailing}/`;
              }

              const normalizedFile = pathWithoutLeadingSlash.replace(/\/+$/, '');
              return normalizedFile;
            })
            .filter(Boolean)
            .join(',')
        : '';
      
      const payload = {
        subject,
        recipient_email,
        body,
        file_paths: normalizedFilePaths,
      };

      if (mode === 'edit' && messageId) {
        await updateMessage(messageId, payload);
        toast.success('Message updated successfully!');
      } else {
        await createMessage(payload);
        toast.success('Message created with success');
      }
      if (onSuccess) {
        onSuccess(mode === 'edit' ? messageId : undefined);
      } else {
        setTimeout(() => {
          navigate('/dashboard');
        }, 1200);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'An error occurred while saving the message';
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttachButtonClick = () => {
    setIsAttachOpen(true);
  };
  const handleCloseAttachModal = () => {
    setIsAttachOpen(false);
  };

  const handleDeleteFile = () => {
    if (fileToDeleteIndex !== null) {
      const newPaths = attachedFiles.filter((_, i) => i !== fileToDeleteIndex);
      setFilePaths(newPaths.join(","));
      setDeleteModalOpen(false);
      setFileToDeleteIndex(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setFileToDeleteIndex(null);
  };

  const handleDiscard = () => {
    if (onDiscard) {
      onDiscard();
    } else {
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    if (isPreview && onDiscard) {
      onDiscard();
    } else {
      navigate('/dashboard');
    }
  };

  const attachedFiles = filePaths ? filePaths.split(',').filter((path) => path.trim()) : [];
  const isPreview = mode === 'preview';
  const isEdit = mode === 'edit';
  // const isCreate = mode === 'create';

  const EditIcon = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.862 3.48725L16.549 1.79925C16.9007 1.44757 17.3777 1.25 17.875 1.25C18.3723 1.25 18.8493 1.44757 19.201 1.79925C19.5527 2.15092 19.7502 2.6279 19.7502 3.12525C19.7502 3.62259 19.5527 4.09957 19.201 4.45125L8.582 15.0702C8.05332 15.5986 7.40137 15.987 6.685 16.2002L4 17.0002L4.8 14.3152C5.01328 13.5989 5.40163 12.9469 5.93 12.4182L14.862 3.48725ZM14.862 3.48725L17.5 6.12525M16 13.0002V17.7502C16 18.347 15.7629 18.9193 15.341 19.3412C14.919 19.7632 14.3467 20.0002 13.75 20.0002H3.25C2.65326 20.0002 2.08097 19.7632 1.65901 19.3412C1.23705 18.9193 1 18.347 1 17.7502V7.25025C1 6.65351 1.23705 6.08121 1.65901 5.65926C2.08097 5.2373 2.65326 5.00025 3.25 5.00025H8" stroke="#FF5400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return Math.round(mb * 100) / 100 + ' MB';
  };

  const getFileSize = (path) => {
    // Find the file in filePathsData array by matching the path
    const fileData = filePathsData.find((file) => {
      const filePath = file.path || '';
      // Match by exact path or by filename
      return filePath === path || filePath.endsWith(path) || path.endsWith(filePath);
    });
    return fileData?.size || null;
  };

  const renderAttachments = () => {
    if (attachedFiles.length === 0) return null;
    
    return (
      <div className="mt-4">
        <h4 className="text-bodyMR text-dark5 font-semibold mb-2">
          Attached Files ({attachedFiles.length})
        </h4>
        <div className="grid grid-cols-1 mobile:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 bg-gray-200 rounded-lg mobile:p-2 p-4">
          {attachedFiles.map((path, index) => {
            const segments = path.split('/').filter(Boolean);
            const fileName = segments.length > 0 ? segments[segments.length - 1] : path;
            const fileSize = getFileSize(path);
            
            return (
              <div
                key={index}
                className="flex flex-col items-center gap-0 rounded bg-white p-2"
              >
                <div className="relative w-full flex justify-center">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <FileIcon fileName={fileName} className="w-14 h-14 object-contain" />
                  </div>
                  {!isPreview && (
                    <button
                      onClick={() => {
                        setFileToDeleteIndex(index);
                        setDeleteModalOpen(true);
                      }}
                      className="absolute top-0 right-0 text-red-500 hover:text-red-700 bg-white rounded-full p-1"
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </div>
                <span className="text-bodyESM text-dark5 truncate w-full text-center">
                  {fileName || path}
                </span>
                {fileSize && (
                  <span className="text-bodyESM text-dark5 truncate w-full text-center">
                    {formatFileSize(fileSize)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white mobile:rounded-none rounded-lg">
      {/* Header - Mobile */}
      {!isPreview && (
        <div className="w-full px-4 py-2 desktop:py-4 desktop:hidden flex justify-between">
          <button onClick={handleDiscard} disabled={loading} className="text-bodyMR desktop:hidden text-dark4 hover:text-red-600 disabled:opacity-50 transition-colors">Discard</button>
          <button onClick={handleApproveClick} disabled={loading} className="flex items-center gap-2 px-6 py-2 bg-primary rounded-lg text-bodyMR text-white hover:bg-opacity-90 disabled:opacity-50 transition-all">
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.4467 1.30469C16.5516 1.2904 16.6582 1.31786 16.7426 1.38184C16.7845 1.41363 16.8196 1.45361 16.8461 1.49902C16.8725 1.54436 16.8898 1.59449 16.8969 1.64648C16.9039 1.69859 16.9005 1.75188 16.8871 1.80273C16.8804 1.82807 16.8712 1.85254 16.8598 1.87598L16.8187 1.94336L7.21913 14.543C7.18458 14.5883 7.14038 14.6254 7.09022 14.6523C7.03996 14.6793 6.98407 14.6954 6.92714 14.6992C6.87035 14.703 6.81342 14.6948 6.76015 14.6748C6.73348 14.6648 6.70778 14.6521 6.68397 14.6367L6.61757 14.583L1.22987 9.19531C1.1593 9.11958 1.12072 9.01951 1.12245 8.91602C1.12428 8.81239 1.16635 8.71294 1.23964 8.63965C1.31283 8.56651 1.41159 8.52438 1.51503 8.52246C1.61658 8.52067 1.71516 8.558 1.79042 8.62598L6.45546 13.2891L6.85975 13.6943L7.20643 13.2393L16.182 1.45801H16.183C16.2472 1.37401 16.3419 1.31899 16.4467 1.30469Z" stroke="white"/>
            </svg>
            {loading ? (isEdit ? 'Saving...' : 'Sending...') : isEdit ? 'Save' : 'Approve'}
          </button>
        </div>
      )}
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isPreview ? (
              <>
                <button 
                  onClick={handleBack} 
                  disabled={loading} 
                  className="desktop:hidden flex items-center gap-2 text-primary text-bodyMR hover:text-primary/80 transition-colors"
                >
                  <img src={`${process.env.PUBLIC_URL}/back.svg`} alt="back" className="w-4 h-4" />
                  Back
                </button>
                <h2 className="text-black text-bodyLB font-normal hidden desktop:block">
                  Message Preview
                </h2>
              </>
            ) : (
              <h2 className="text-black text-bodyLB font-normal">
                {isEdit ? 'Edit Message' : 'New Message'}
              </h2>
            )}
          </div>
          {isPreview && onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 text-primary text-bodyMR"
            >
              <EditIcon />
              <span className="">Edit message</span>
            </button>
          )}
        </div>
      </div>

      {/* Form Body */}
      <div className="flex-1 overflow-y-auto py-6 px-4">
        <div className="space-y-4">

          {/* To Field */}
          <div className="border-b-2 border-gray2 pb-2">
            <div className="flex items-center gap-2">
              <label className="text-bodySR text-black font-normal min-w-[50px]">
                To:
              </label>
              {isPreview ? (
                <span className="flex-1 text-bodySR text-dark5">{formData.recipient_email || '—'}</span>
              ) : (
                <input
                  type="email"
                  name="recipient_email"
                  id="recipient_email"
                  value={formData.recipient_email}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="flex-1 text-bodySR text-dark5 border-0 p-0 focus:ring-0 focus:outline-none bg-transparent"
                  placeholder="recipient@example.com"
                />
              )}
            </div>
          </div>

          {/* Subject Field */}
          <div className="border-b-2 border-gray2 pb-2">
            <div className="flex items-center gap-2">
              <label className="text-bodySR text-black font-normal min-w-[50px]">
                Subject:
              </label>
              {isPreview ? (
                <span className="flex-1 text-bodySR text-dark5">{formData.subject || '—'}</span>
              ) : (
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="flex-1 text-bodySR text-dark5 border-0 p-0 focus:ring-0 focus:outline-none bg-transparent"
                  placeholder="Enter subject"
                />
              )}
            </div>
          </div>

          {/* Body Field */}
          <div className="mt-6">
            {isPreview ? (
              <div className="w-full min-h-[200px] border-2 border-gray2 rounded-lg p-4 text-bodySR text-black whitespace-pre-wrap">
                {formData.body || '—'}
              </div>
            ) : (
              <textarea
                name="body"
                id="body"
                value={formData.body}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full h-64 border-2 border-gray2 rounded-lg p-4 text-bodySR text-black focus:ring-0 focus:border-gray2 resize-none"
                placeholder="Write your message here..."
              />
            )}
          </div>

          {/* Attached Files Display */}
          {renderAttachments()}
        </div>
      </div>

      {/* Footer Actions - Only for edit and create modes */}
      {!isPreview && (
        <div className="border-t p-6 flex items-center justify-between flex-row-reverse">
          <button
            onClick={handleDiscard}
            disabled={loading}
            className="text-bodyMR mobile:hidden text-dark4 hover:text-red-600 disabled:opacity-50 transition-colors"
          >
            Discard
          </button>
          
          <div className="flex items-center gap-3 mobile:gap-2">
            <button
              onClick={handleApproveClick}
              disabled={loading}
              className="flex mobile:hidden items-center gap-2 px-6 py-2 bg-primary rounded-lg text-bodyMR text-white hover:bg-opacity-90 disabled:opacity-50 transition-all"
            >
              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.4467 1.30469C16.5516 1.2904 16.6582 1.31786 16.7426 1.38184C16.7845 1.41363 16.8196 1.45361 16.8461 1.49902C16.8725 1.54436 16.8898 1.59449 16.8969 1.64648C16.9039 1.69859 16.9005 1.75188 16.8871 1.80273C16.8804 1.82807 16.8712 1.85254 16.8598 1.87598L16.8187 1.94336L7.21913 14.543C7.18458 14.5883 7.14038 14.6254 7.09022 14.6523C7.03996 14.6793 6.98407 14.6954 6.92714 14.6992C6.87035 14.703 6.81342 14.6948 6.76015 14.6748C6.73348 14.6648 6.70778 14.6521 6.68397 14.6367L6.61757 14.583L1.22987 9.19531C1.1593 9.11958 1.12072 9.01951 1.12245 8.91602C1.12428 8.81239 1.16635 8.71294 1.23964 8.63965C1.31283 8.56651 1.41159 8.52438 1.51503 8.52246C1.61658 8.52067 1.71516 8.558 1.79042 8.62598L6.45546 13.2891L6.85975 13.6943L7.20643 13.2393L16.182 1.45801H16.183C16.2472 1.37401 16.3419 1.31899 16.4467 1.30469Z" stroke="white"/>
              </svg>
              {loading ? (isEdit ? "Saving..." : "Sending...") : isEdit ? "Save" : "Approve"}
            </button>
            
            <button
              onClick={handleAttachButtonClick}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-bodyMR text-dark5 hover:bg-gray-50 disabled:opacity-50 transition-all"
            >
              <img src={`${process.env.PUBLIC_URL}/clip.svg`} alt="Attach" className="w-5 h-5" />
              Attach file
            </button>
          </div>
        </div>
      )}

      {/* Attach Files Modal */}
      {isAttachOpen && (
        <ModalAttachFiles
          titleModal="Attach a file"
          onSelectFolders={handleFolderSelect}
          closeModal={handleCloseAttachModal}
          initialSelection={attachedFiles}
        />
      )}

      {/* Delete File Confirmation Modal */}
      {deleteModalOpen && fileToDeleteIndex !== null && (
        <ModalDeleteFile
          isOpen={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          item={{
            id: attachedFiles[fileToDeleteIndex],
            name: attachedFiles[fileToDeleteIndex]?.split('/').pop() || 'this file',
            type: 'file'
          }}
          onDelete={handleDeleteFile}
        />
      )}
    </div>
  );
};

export default MessageForm;
